import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { colors, inr } from "@/theme";
import { SectionTitle } from "@/components/common";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, BACKEND_URL } from "@/lib/api";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { toast } from "sonner";

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [form, setForm] = useState({ shipping_name: user?.name || "", shipping_phone: user?.mobile || "", shipping_address: "" });
  const [coupon, setCoupon] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [giftWrap, setGiftWrap] = useState(false);
  const [giftNote, setGiftNote] = useState("");
  const [points, setPoints] = useState(null);
  const [redeem, setRedeem] = useState(false);
  const [busy, setBusy] = useState(false);
  const [placed, setPlaced] = useState(false);

  useEffect(() => { if (items.length === 0 && !placed) navigate("/cart", { replace: true }); }, [items, navigate, placed]);
  useEffect(() => { api("/points").then(setPoints).catch(() => {}); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    try {
      const res = await api("/coupons/apply", { method: "POST", body: { code: coupon.trim(), subtotal } });
      setAppliedCoupon(res);
      toast.success(`Coupon applied · −${inr(res.discount)}`);
    } catch (e) { setAppliedCoupon(null); toast.error(e.message); }
  };

  const couponDiscount = appliedCoupon?.discount || 0;
  const afterCoupon = Math.max(0, subtotal - couponDiscount);
  const redeemValue = 0.1;
  const maxRedeemPoints = points ? Math.min(points.balance, Math.floor((afterCoupon * 0.3) / redeemValue)) : 0;
  const pointsDiscount = redeem ? Math.round(maxRedeemPoints * redeemValue) : 0;
  const giftFee = giftWrap ? 49 : 0;
  const total = Math.max(0, afterCoupon - pointsDiscount + giftFee);

  const placeOrder = async () => {
    if (!form.shipping_name || !form.shipping_phone || !form.shipping_address) return toast("Please fill in all shipping details");
    setBusy(true);
    try {
      const res = await api("/orders/create", {
        method: "POST",
        body: {
          items: items.map((i) => ({ product_id: i.product.id, quantity: i.quantity })),
          shipping_address: form.shipping_address,
          shipping_name: form.shipping_name,
          shipping_phone: form.shipping_phone,
          points_to_redeem: redeem ? maxRedeemPoints : 0,
          coupon_code: appliedCoupon ? coupon.trim() : "",
          gift_wrap: giftWrap,
          gift_note: giftNote,
        },
      });
      const orderId = res.order.id;
      setPlaced(true);
      if (res.mock) {
        await api(`/orders/${orderId}/mock-pay`, { method: "POST" });
        clear();
        toast.success("Order placed successfully");
        navigate(`/order/${orderId}`, { replace: true });
      } else {
        clear();
        window.location.href = res.checkout_url.startsWith("http") ? res.checkout_url : `${BACKEND_URL}${res.checkout_url}`;
      }
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="fade-up" style={{ padding: isMobile ? "24px 16px 40px" : "40px 24px 56px" }}>
      <div className="checkout-narrow">
        <SectionTitle title="Checkout" />
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          <Section title="Shipping Details">
            <Field label="Full Name"><input data-testid="checkout-name" className="input-field" value={form.shipping_name} onChange={set("shipping_name")} /></Field>
            <Field label="Phone"><input data-testid="checkout-phone" className="input-field" value={form.shipping_phone} onChange={set("shipping_phone")} /></Field>
            <Field label="Address"><textarea data-testid="checkout-address" className="input-field" rows={3} value={form.shipping_address} onChange={set("shipping_address")} placeholder="Flat, street, city, PIN" style={{ resize: "vertical" }} /></Field>
          </Section>

          <Section title="Coupon">
            <div style={{ display: "flex", gap: 10 }}>
              <input data-testid="coupon-input" className="input-field" value={coupon} onChange={(e) => setCoupon(e.target.value)} placeholder="Enter code" />
              <button data-testid="coupon-apply" className="btn-outline" onClick={applyCoupon} type="button">Apply</button>
            </div>
          </Section>

          {points && points.balance > 0 && maxRedeemPoints > 0 && (
            <Section title="Nest Rewards">
              <label style={{ display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}>
                <input data-testid="redeem-points" type="checkbox" checked={redeem} onChange={(e) => setRedeem(e.target.checked)} style={{ width: 18, height: 18 }} />
                <span className="font-body" style={{ fontSize: 14 }}>Redeem {maxRedeemPoints} points (−{inr(Math.round(maxRedeemPoints * redeemValue))})</span>
              </label>
            </Section>
          )}

          <Section title="Gift Wrap">
            <label style={{ display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}>
              <input data-testid="gift-wrap" type="checkbox" checked={giftWrap} onChange={(e) => setGiftWrap(e.target.checked)} style={{ width: 18, height: 18 }} />
              <span className="font-body" style={{ fontSize: 14 }}>Add gift wrap (+₹49)</span>
            </label>
            {giftWrap && <textarea data-testid="gift-note" className="input-field" rows={2} placeholder="Gift note (optional)" value={giftNote} onChange={(e) => setGiftNote(e.target.value)} style={{ marginTop: 10, resize: "vertical" }} />}
          </Section>

          <div style={{ background: colors.surfaceSecondary, borderRadius: 12, padding: 24 }}>
            <h3 className="font-display" style={{ fontSize: 22, fontWeight: 700, margin: "0 0 16px" }}>Order Summary</h3>
            <SumRow label="Subtotal" value={inr(subtotal)} />
            {couponDiscount > 0 && <SumRow label={`Coupon (${appliedCoupon.code})`} value={`−${inr(couponDiscount)}`} accent />}
            {pointsDiscount > 0 && <SumRow label="Points redeemed" value={`−${inr(pointsDiscount)}`} accent />}
            {giftFee > 0 && <SumRow label="Gift wrap" value={inr(giftFee)} />}
            <div style={{ borderTop: `1px solid ${colors.border}`, margin: "14px 0" }} />
            <SumRow label="Total Payable" value={inr(total)} bold />
            <button data-testid="place-order" className="btn-brand" style={{ width: "100%", marginTop: 20 }} onClick={placeOrder} disabled={busy}>
              {busy ? "Placing order…" : `Pay ${inr(total)}`}
            </button>
            <p className="font-body" style={{ textAlign: "center", fontSize: 12, color: colors.mutedText, marginTop: 12 }}>Secure checkout · Razorpay</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ border: `1px solid ${colors.border}`, borderRadius: 12, padding: 20 }}>
      <div className="eyebrow" style={{ marginBottom: 14 }}>{title}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>{children}</div>
    </div>
  );
}
function Field({ label, children }) {
  return <div><label className="font-body" style={{ display: "block", fontSize: 13, color: colors.mutedText, marginBottom: 6 }}>{label}</label>{children}</div>;
}
function SumRow({ label, value, bold, accent }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0" }}>
      <span className="font-body" style={{ fontSize: bold ? 17 : 14, fontWeight: bold ? 700 : 400, color: colors.onSurface }}>{label}</span>
      <span className="font-body" style={{ fontSize: bold ? 17 : 14, fontWeight: bold ? 700 : 500, color: accent ? colors.brand : colors.onSurface }}>{value}</span>
    </div>
  );
}
